import {
  useAdminFeatures,
  useFeatureAdminActions,
  useTenantTypeFeatureMatrix
} from '@lib/features/useFeatureAdmin'
import { usePlanFeatureMatrix } from '@lib/stripe/useStripeAdmin'
import { getTenantTypeLabel } from '@lib/tenantTypes'
import {
  Alert,
  Center,
  Group,
  Stack,
  Switch,
  Text,
  Tooltip
} from '@mantine/core'
import { H3, SortableTable } from '@packages/ui'
import { IconCircleCheck } from '@tabler/icons-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

type Props = {
  tenantId: string
  tenantType: string
  billingPlan?: string | null
}

type MatrixRow = {
  feature_key: string
  feature_name: string
  in_icp: boolean
  in_plan: boolean
  has_override: boolean
  override_enabled: boolean
  custom_additive: boolean
}

function effectiveEnabled(row: MatrixRow): boolean {
  if (row.has_override) return row.override_enabled
  if (row.custom_additive) return true
  return row.in_plan
}

function CheckCell({ on }: { on: boolean }) {
  return (
    <Center>
      {on ? (
        <IconCircleCheck size={20} stroke={2} className="text-teal-400" />
      ) : (
        <Text c="dimmed" size="sm">
          —
        </Text>
      )}
    </Center>
  )
}

export function TenantFeatureMatrix({
  tenantId,
  tenantType,
  billingPlan
}: Props) {
  const {
    fetchTenantFeatureOverrides,
    fetchTenantCustomFeatures,
    setTenantCustomFeatures,
    setTenantFeatureOverride,
    clearTenantFeatureOverride
  } = useFeatureAdminActions()
  const { data: allFeatures = [], isLoading: featuresLoading } =
    useAdminFeatures()
  const { data: icpMatrix = [] } = useTenantTypeFeatureMatrix()
  const { data: planMatrix = [], isLoading: planMatrixLoading } =
    usePlanFeatureMatrix()

  const [overrideRows, setOverrideRows] = useState<
    Awaited<ReturnType<typeof fetchTenantFeatureOverrides>>
  >([])
  const [customKeys, setCustomKeys] = useState<Set<string>>(new Set())
  const [loadingExtras, setLoadingExtras] = useState(true)
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const planLabel = billingPlan?.trim() || 'basic'
  const icpLabel = getTenantTypeLabel(tenantType)
  const catalogType = tenantType === 'default' ? '' : tenantType

  const icpFeatureKeys = useMemo(
    () =>
      new Set(
        icpMatrix
          .filter((row) => row.tenant_type === catalogType && row.enabled)
          .map((row) => row.feature_key)
      ),
    [icpMatrix, catalogType]
  )

  const planFeatureKeys = useMemo(
    () =>
      new Set(
        planMatrix
          .filter(
            (row) =>
              row.tenant_type === catalogType && row.plan_tier === planLabel
          )
          .map((row) => row.feature_key)
      ),
    [planMatrix, catalogType, planLabel]
  )

  const loadTenantState = useCallback(async () => {
    setLoadingExtras(true)
    setError(null)
    try {
      const [overrides, custom] = await Promise.all([
        fetchTenantFeatureOverrides(tenantId),
        fetchTenantCustomFeatures(tenantId)
      ])
      setOverrideRows(overrides)
      setCustomKeys(
        new Set(custom.filter((row) => row.enabled).map((row) => row.feature_key))
      )
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load tenant features'
      )
    } finally {
      setLoadingExtras(false)
    }
  }, [tenantId, fetchTenantFeatureOverrides, fetchTenantCustomFeatures])

  useEffect(() => {
    void loadTenantState()
  }, [loadTenantState])

  const rows = useMemo((): MatrixRow[] => {
    const overrideByKey = new Map(
      overrideRows.map((row) => [row.feature_key, row])
    )

    return allFeatures
      .filter((f) => !f.disabled)
      .map((f) => {
        const override = overrideByKey.get(f.key)
        return {
          feature_key: f.key,
          feature_name: f.name,
          in_icp: icpFeatureKeys.has(f.key),
          in_plan: planFeatureKeys.has(f.key),
          has_override: override?.has_override ?? false,
          override_enabled: override?.override_enabled ?? false,
          custom_additive: customKeys.has(f.key)
        }
      })
      .sort((a, b) => {
        const score = (r: MatrixRow) =>
          (r.in_icp ? 4 : 0) + (r.in_plan ? 2 : 0) + (effectiveEnabled(r) ? 1 : 0)
        const diff = score(b) - score(a)
        if (diff !== 0) return diff
        return a.feature_name.localeCompare(b.feature_name)
      })
  }, [allFeatures, overrideRows, customKeys, icpFeatureKeys, planFeatureKeys])

  const updateCustomKeys = async (next: Set<string>) => {
    await setTenantCustomFeatures(tenantId, [...next])
    setCustomKeys(next)
  }

  const applyCustom = async (row: MatrixRow, on: boolean) => {
    setSavingKey(row.feature_key)
    setError(null)
    try {
      if (row.in_icp) {
        const nextCustom = new Set(customKeys)
        if (nextCustom.has(row.feature_key)) {
          nextCustom.delete(row.feature_key)
          await updateCustomKeys(nextCustom)
        }

        if (on === row.in_plan) {
          await clearTenantFeatureOverride(tenantId, row.feature_key)
        } else if (on) {
          await setTenantFeatureOverride(tenantId, row.feature_key, true)
        } else {
          await setTenantFeatureOverride(tenantId, row.feature_key, false)
        }
      } else {
        if (row.has_override) {
          await clearTenantFeatureOverride(tenantId, row.feature_key)
        }
        const nextCustom = new Set(customKeys)
        if (on) nextCustom.add(row.feature_key)
        else nextCustom.delete(row.feature_key)
        await updateCustomKeys(nextCustom)
      }
      await loadTenantState()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSavingKey(null)
    }
  }

  const loading = featuresLoading || planMatrixLoading || loadingExtras

  const summary = useMemo(() => {
    const effective = rows.filter((row) => effectiveEnabled(row)).length
    const baseline = rows.filter((row) => row.in_icp && row.in_plan).length
    const customized = rows.filter(
      (row) =>
        row.has_override ||
        row.custom_additive ||
        effectiveEnabled(row) !== row.in_plan
    ).length
    return { effective, total: rows.length, baseline, customized }
  }, [rows])

  return (
    <Stack gap="md">
      <Stack gap={4}>
        <H3>Feature matrix</H3>
        <Text size="sm" c="dimmed">
          <strong>{icpLabel}</strong> ICP and <strong>{planLabel}</strong> plan
          set the baseline. Toggle <strong>Custom</strong> to enable features
          beyond the plan (within ICP via override, outside ICP via tenant
          add-on).
        </Text>
        {!loading && (
          <Text size="sm" c="dimmed">
            {summary.effective} / {summary.total} effective · {summary.baseline}{' '}
            in ICP ∩ plan
            {summary.customized > 0
              ? ` · ${summary.customized} customized`
              : ''}
          </Text>
        )}
      </Stack>

      {error && (
        <Alert color="red" title="Error">
          {error}
        </Alert>
      )}

      <SortableTable
        data={rows}
        loading={loading}
        defaultSortBy="feature_name"
        defaultSortAsc
        highlightOnHover
        noResultsMessage="No features in catalog."
        columns={[
          { key: 'feature_name', label: 'Feature' },
          {
            key: 'feature_key',
            label: 'Key',
            render: (value: string) => (
              <Text ff="monospace" size="sm">
                {value}
              </Text>
            )
          },
          {
            key: 'in_icp',
            label: 'ICP',
            sortable: false,
            render: (value: boolean) => <CheckCell on={value} />
          },
          {
            key: 'in_plan',
            label: 'Plan',
            sortable: false,
            render: (value: boolean) => <CheckCell on={value} />
          },
          {
            key: 'feature_key',
            label: 'Custom',
            sortable: false,
            render: (_value: string, row: MatrixRow) => {
              const hint =
                !row.in_icp && !row.custom_additive && !effectiveEnabled(row)
                  ? 'Tenant add-on (outside ICP ceiling)'
                  : row.has_override
                    ? 'Plan override'
                    : row.custom_additive
                      ? 'Tenant add-on'
                      : undefined

              return (
                <Group gap="xs" wrap="nowrap">
                  <Switch
                    checked={effectiveEnabled(row)}
                    disabled={savingKey === row.feature_key}
                    onChange={(e) =>
                      void applyCustom(row, e.currentTarget.checked)
                    }
                    aria-label={`Custom ${row.feature_key}`}
                  />
                  {hint && (
                    <Tooltip label={hint}>
                      <Text size="xs" c="dimmed">
                        {row.custom_additive ? 'add-on' : 'override'}
                      </Text>
                    </Tooltip>
                  )}
                </Group>
              )
            }
          }
        ]}
      />
    </Stack>
  )
}
